import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

// 自前で撮った素材の置き場。
//
// 取り込み（cmd/import-store-media）はフォルダ名が1店舗に定まったときだけ
// 紐付ける。定まらなかったものは店舗が空のままここに来て、人が選ぶ。
// 「見たが紐付け先が無かった」も判定として記録する。そうしないと作業リストが
// 永久に減らず、同じ素材を何度も見ることになる。

const PAGE_SIZE = 30;

const ASSIGNMENTS = [
  { value: 'todo', label: '未判定（店舗なし）' },
  { value: 'assigned', label: '紐付け済み' },
  { value: 'reviewed', label: '店舗なしで確定' },
  { value: '', label: 'すべて' },
];

const KINDS = [
  { value: '', label: 'すべて' },
  { value: 'video', label: '動画' },
  { value: 'photo', label: '写真' },
  { value: 'document', label: '資料' },
  { value: 'other', label: 'その他' },
];

const KIND_LABEL = { video: '動画', photo: '写真', document: '資料', other: 'その他' };

function humanSize(bytes) {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)}${units[i]}`;
}

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ja-JP');
}

// 撮影日時。フォルダ名から読んだ日付には時刻が無いので、出しかたを変える。
//
// 00:00 を「深夜0時に撮った」と読まれるのが困る。フォルダ由来は日付だけ出し、
// ファイルのメタデータ由来のときだけ時刻まで出す。
function formatCapturedAt(value, source) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  if (source === 'metadata') {
    return d.toLocaleString('ja-JP', {
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }
  return d.toLocaleDateString('ja-JP');
}

// StorePicker searches the store master by name. It opens pre-filled with the
// name the folder claimed, because that is nearly always the right search —
// the reason the import failed is usually a suffix or a spelling, not a
// different store.
function StorePicker({ item, onPick, onCancel }) {
  const [keyword, setKeyword] = useState(item.storeHint || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // 都道府県はフォルダの申告。外すと同名店が全国から出るので既定では効かせる。
  const [usePrefecture, setUsePrefecture] = useState(Boolean(item.prefectureHint));

  useEffect(() => {
    const ac = new AbortController();
    const timer = setTimeout(async () => {
      const q = keyword.trim();
      if (!q) {
        setResults([]);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const url = new URL(`${API_BASE_URL}/admin/stores`);
        url.searchParams.set('q', q);
        url.searchParams.set('limit', '10');
        if (usePrefecture && item.prefectureHint) {
          url.searchParams.set('prefecture', item.prefectureHint);
        }
        const res = await apiFetch(url.toString(), { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        setResults(Array.isArray(body.data) ? body.data : []);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Network Error');
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [keyword, usePrefecture, item.prefectureHint]);

  return (
    <div className="border rounded p-2 bg-light">
      <div className="d-flex gap-2 align-items-center mb-2">
        <input
          className="form-control form-control-sm"
          autoFocus
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="店舗名で検索"
        />
        <button className="btn btn-sm btn-outline-secondary" onClick={onCancel}>
          閉じる
        </button>
      </div>
      {item.prefectureHint && (
        <div className="form-check form-check-inline small mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            id={`pref-${item.id}`}
            checked={usePrefecture}
            onChange={(e) => setUsePrefecture(e.target.checked)}
          />
          <label className="form-check-label" htmlFor={`pref-${item.id}`}>
            {item.prefectureHint} に絞る
          </label>
        </div>
      )}
      {error && <div className="text-danger small mb-1">{error}</div>}
      {loading && <div className="text-muted small">検索中…</div>}
      {!loading && keyword.trim() && results.length === 0 && (
        <div className="text-muted small">
          該当なし。店舗マスタに無い可能性があります（「店舗なしで確定」で作業リストから外せます）。
        </div>
      )}
      <div className="list-group list-group-flush">
        {results.map((s) => (
          <button
            key={s.id}
            className="list-group-item list-group-item-action py-1 px-2 text-start"
            onClick={() => onPick(s)}
          >
            <div className="fw-semibold small">{s.name}</div>
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
              {s.prefecture} {s.address}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// iPhoneの写真は HEIC で、**Chrome も Firefox も表示できない**（Safariのみ）。
// 素材の227枚がこれ。<img> を出すと壊れた画像が並ぶだけなので、開くための
// リンクにする。サムネイルを作るのは別の作業。
const isHeic = (item) =>
  /\.heic$/i.test(item.originalName || '') || item.contentType === 'image/heic';

function Preview({ item }) {
  if (!item.url) return <div className="text-muted small">配信URLなし</div>;
  // 変換済みならそれを出す。元のHEICは原本として残っているので、
  // 開くリンクは元のままにする。
  if (item.previewUrl) {
    return (
      <a href={item.url} target="_blank" rel="noreferrer" title={item.originalName}>
        <img
          src={item.previewUrl}
          alt={item.originalName}
          className="rounded"
          style={{ width: 160, height: 110, objectFit: 'cover' }}
        />
      </a>
    );
  }
  if (item.kind === 'photo' && isHeic(item)) {
    return (
      <div
        className="rounded border d-flex flex-column align-items-center justify-content-center bg-light"
        style={{ width: 160, height: 110 }}
      >
        <div className="small text-muted">HEIC</div>
        <div className="text-muted" style={{ fontSize: '0.7rem' }}>
          未変換
        </div>
        <a href={item.url} target="_blank" rel="noreferrer" className="small">
          ダウンロード
        </a>
      </div>
    );
  }
  if (item.kind === 'photo') {
    return (
      <a href={item.url} target="_blank" rel="noreferrer">
        <img
          src={item.url}
          alt={item.originalName}
          className="rounded"
          style={{ width: 160, height: 110, objectFit: 'cover' }}
        />
      </a>
    );
  }
  if (item.kind === 'video') {
    // preload="metadata": 一覧に何十本も並ぶので本体は落とさない。
    return (
      <video
        src={item.url}
        controls
        preload="metadata"
        className="rounded bg-dark"
        style={{ width: 160, height: 110, objectFit: 'contain' }}
      />
    );
  }
  return (
    <a href={item.url} target="_blank" rel="noreferrer" className="small">
      開く
    </a>
  );
}

export default function StoreMedia() {
  useAuthGuard();

  const [assignment, setAssignment] = useState('todo');
  const [kind, setKind] = useState('');
  const [category, setCategory] = useState('');
  const [prefecture, setPrefecture] = useState('');
  const [storeHint, setStoreHint] = useState('');
  const [keyword, setKeyword] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [categories, setCategories] = useState([]);
  const [prefectures, setPrefectures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [pickingId, setPickingId] = useState(null);
  const [unresolved, setUnresolved] = useState(null);
  // 「まとめて」で開いている未解決グループ。1つずつしか開かない。
  const [groupTarget, setGroupTarget] = useState(null);
  const [groupBusy, setGroupBusy] = useState(false);
  const [groupDone, setGroupDone] = useState('');

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      const url = new URL(`${API_BASE_URL}/admin/store-media`);
      url.searchParams.set('limit', String(PAGE_SIZE));
      url.searchParams.set('offset', String(offset));
      if (assignment) url.searchParams.set('assignment', assignment);
      if (kind) url.searchParams.set('kind', kind);
      if (category) url.searchParams.set('category', category);
      if (prefecture) url.searchParams.set('prefecture', prefecture);
      if (storeHint) url.searchParams.set('storeHint', storeHint);
      if (query.trim()) url.searchParams.set('q', query.trim());

      const res = await apiFetch(url.toString(), { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setItems(Array.isArray(body.data) ? body.data : []);
      setTotal(body.meta?.total || 0);
      setCounts(body.meta?.counts || {});
      setCategories(body.meta?.categories || []);
      setPrefectures(body.meta?.prefectures || []);
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message || 'Network Error');
    } finally {
      setLoading(false);
    }
  }, [assignment, kind, category, prefecture, storeHint, query, offset]);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const loadUnresolved = async () => {
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/store-media/unresolved-stores?limit=80`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setUnresolved(Array.isArray(body.data) ? body.data : []);
    } catch (e) {
      setError(e.message || 'Network Error');
    }
  };

  // 判定した行はその場で消す。再取得を待つと数クリックごとに位置を見失う。
  const review = async (item, payload, removes) => {
    setBusyId(item.id);
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/store-media/${item.id}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || `HTTP ${res.status}`);
      }
      const body = await res.json();
      setPickingId(null);
      if (removes) {
        setItems((prev) => prev.filter((x) => x.id !== item.id));
        setTotal((t) => Math.max(0, t - 1));
      } else {
        const next = body.data?.media || {};
        setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, ...next, url: x.url } : x)));
      }
    } catch (e) {
      setError(e.message || 'Network Error');
    } finally {
      setBusyId(null);
    }
  };

  // フォルダ単位の判定。素材は86件・80件とフォルダで来るので、
  // 1件ずつ押させると同じ判断を80回させることになる。
  const reviewGroup = async (group, payload) => {
    setGroupBusy(true);
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/store-media/review-group`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prefecture: group.prefecture || '',
          storeHint: group.storeHint,
          ...payload,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || `HTTP ${res.status}`);
      }
      const body = await res.json();
      const n = body.data?.updated ?? 0;
      setGroupDone(`${group.storeHint}: ${n}件を更新しました`);
      setGroupTarget(null);
      // 更新した群は一覧から消える（店舗が付くため）。両方引き直す。
      await loadUnresolved();
      await load();
    } catch (e) {
      setError(e.message || 'Network Error');
    } finally {
      setGroupBusy(false);
    }
  };

  const assign = (item, store) =>
    review(item, { storeId: store.id }, assignment === 'todo' || assignment === 'reviewed');

  const markNoStore = (item) =>
    review(item, { storeId: '', reviewed: true }, assignment === 'todo' || assignment === 'assigned');

  const reopen = (item) =>
    review(item, { storeId: '', reviewed: false }, assignment !== '' && assignment !== 'todo');

  const done = useMemo(
    () => (counts.assigned || 0) + (counts.reviewed || 0),
    [counts],
  );

  const filtersChanged = (fn) => (e) => {
    setOffset(0);
    fn(e.target.value);
  };

  return (
    <Layout>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="mb-1">自前撮影素材</h4>
          <p className="text-muted mb-0 small">
            <code>import-store-media</code> で取り込んだ動画・写真。フォルダ名から店舗が
            決まらなかったものを、ここで店舗に紐付けます。紐付け先が無い素材は
            「店舗なしで確定」で作業リストから外れます（消えはしません）。
          </p>
        </div>
        <div className="text-end small text-muted">
          <div>
            判定済み <strong>{done}</strong> / 未判定 <strong>{counts.todo || 0}</strong>
          </div>
          <div>
            全 {counts.total || 0}件（動画 {counts.video || 0} / 写真 {counts.photo || 0}）
            {counts.sizeBytes ? ` ${humanSize(counts.sizeBytes)}` : ''}
          </div>
          <div>素材を持つ店舗 {counts.storesHeld || 0}件</div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body d-flex flex-wrap gap-3 align-items-end">
          <div>
            <label className="form-label small mb-1">状態</label>
            <select
              className="form-select form-select-sm"
              value={assignment}
              onChange={filtersChanged(setAssignment)}
            >
              {ASSIGNMENTS.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label small mb-1">種別</label>
            <select className="form-select form-select-sm" value={kind} onChange={filtersChanged(setKind)}>
              {KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label small mb-1">用途</label>
            <select
              className="form-select form-select-sm"
              value={category}
              onChange={filtersChanged(setCategory)}
            >
              <option value="">すべて</option>
              {categories.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.value}（{c.count}）
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label small mb-1">都道府県（フォルダ）</label>
            <select
              className="form-select form-select-sm"
              value={prefecture}
              onChange={filtersChanged(setPrefecture)}
            >
              <option value="">すべて</option>
              {prefectures.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.value}（{p.count}）
                </option>
              ))}
            </select>
          </div>
          <div className="flex-grow-1" style={{ minWidth: 200 }}>
            <label className="form-label small mb-1">ファイル名・パス</label>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setOffset(0);
                setQuery(keyword);
              }}
            >
              <input
                className="form-control form-control-sm"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Enterで検索"
              />
            </form>
          </div>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => (unresolved ? setUnresolved(null) : loadUnresolved())}
          >
            {unresolved ? '未解決の店名を閉じる' : '未解決の店名'}
          </button>
        </div>
      </div>

      {storeHint && (
        <div className="alert alert-secondary py-2 d-flex justify-content-between align-items-center">
          <span className="small">
            店名フォルダ「<strong>{storeHint}</strong>」で絞り込み中
          </span>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              setOffset(0);
              setStoreHint('');
            }}
          >
            解除
          </button>
        </div>
      )}

      {unresolved && (
        <div className="card mb-3">
          <div className="card-header py-2 small">
            店舗が決まっていない素材の店名（本数順）。上から埋めるほど素材が動きます。
          </div>
          <div className="card-body p-0">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>都道府県</th>
                  <th>店名（フォルダ）</th>
                  <th className="text-end">素材</th>
                  <th className="text-end">動画</th>
                  <th className="text-end">未判定</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {unresolved.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-muted small">
                      未解決の店名はありません。
                    </td>
                  </tr>
                )}
                {unresolved.map((u) => (
                  <tr key={`${u.prefecture || ''}/${u.storeHint}`}>
                    <td className="small">{u.prefecture || '—'}</td>
                    <td className="small">{u.storeHint}</td>
                    <td className="text-end small">{u.files}</td>
                    <td className="text-end small">{u.videos}</td>
                    <td className="text-end small">{u.todo}</td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-link py-0"
                        onClick={() => {
                          setOffset(0);
                          setAssignment('');
                          setStoreHint(u.storeHint);
                        }}
                      >
                        この素材を見る
                      </button>
                      <button
                        className="btn btn-sm btn-primary py-0 ms-2"
                        disabled={groupBusy}
                        onClick={() =>
                          setGroupTarget(
                            groupTarget?.storeHint === u.storeHint ? null : u,
                          )
                        }
                      >
                        まとめて紐付ける
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary py-0 ms-2"
                        disabled={groupBusy}
                        onClick={() => {
                          if (
                            !window.confirm(
                              `「${u.storeHint}」の未紐付け${u.files}件を「店舗なし」で確定します。よろしいですか？`,
                            )
                          ) {
                            return;
                          }
                          reviewGroup(u, { storeId: '', reviewed: true });
                        }}
                      >
                        まとめて店舗なし
                      </button>
                    </td>
                  </tr>
                ))}
                {groupTarget && (
                  <tr key="group-picker">
                    <td colSpan={6}>
                      <div className="small mb-1">
                        <strong>{groupTarget.storeHint}</strong>（
                        {groupTarget.prefecture || '都道府県なし'}）の未紐付け
                        <strong> {groupTarget.files}件</strong> をまとめて紐付けます。
                        <span className="text-muted">
                          {' '}
                          既に手で紐付けた素材は変わりません。
                        </span>
                      </div>
                      <StorePicker
                        item={{
                          id: `group-${groupTarget.storeHint}`,
                          storeHint: groupTarget.storeHint,
                          prefectureHint: groupTarget.prefecture,
                        }}
                        onPick={(store) => reviewGroup(groupTarget, { storeId: store.id })}
                        onCancel={() => setGroupTarget(null)}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {error && <div className="alert alert-danger py-2 small">{error}</div>}
      {groupDone && (
        <div className="alert alert-success py-2 small d-flex justify-content-between align-items-center">
          <span>{groupDone}</span>
          <button className="btn-close" onClick={() => setGroupDone('')} aria-label="閉じる" />
        </div>
      )}

      <div className="card">
        <div className="card-body p-0">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th style={{ width: 175 }}>素材</th>
                <th>ファイル / 取り込み元</th>
                <th style={{ width: 320 }}>店舗</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={3} className="text-muted small">
                    読み込み中…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-muted small">
                    該当する素材がありません。
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <Preview item={item} />
                    </td>
                    <td>
                      <div className="fw-semibold small">{item.originalName}</div>
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {item.sourcePath}
                      </div>
                      <div className="mt-1 d-flex flex-wrap gap-1">
                        <span className="badge bg-secondary-subtle text-secondary-emphasis">
                          {KIND_LABEL[item.kind] || item.kind}
                        </span>
                        {item.category && (
                          <span className="badge bg-info-subtle text-info-emphasis">{item.category}</span>
                        )}
                        {item.prefectureHint && (
                          <span className="badge bg-light text-dark border">{item.prefectureHint}</span>
                        )}
                        {item.capturedAt && (
                          <span
                            className="badge bg-light text-dark border"
                            title={
                              item.capturedAtSource === 'metadata'
                                ? 'ファイルのメタデータから読んだ撮影日時'
                                : 'フォルダ名の日付。時刻は入っていない'
                            }
                          >
                            撮影 {formatCapturedAt(item.capturedAt, item.capturedAtSource)}
                            {item.capturedAtSource !== 'metadata' && '（日付のみ）'}
                          </span>
                        )}
                        {item.deviceModel && (
                          <span className="badge bg-light text-dark border">{item.deviceModel}</span>
                        )}
                        {item.sizeBytes ? (
                          <span className="badge bg-light text-dark border">{humanSize(item.sizeBytes)}</span>
                        ) : null}
                      </div>
                      {item.notes && <div className="text-muted small mt-1">{item.notes}</div>}
                    </td>
                    <td>
                      {item.storeName ? (
                        <div>
                          <div className="fw-semibold small">{item.storeName}</div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {item.storePrefecture}
                          </div>
                          <div className="mt-1 d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-secondary py-0"
                              disabled={busyId === item.id}
                              onClick={() => setPickingId(pickingId === item.id ? null : item.id)}
                            >
                              変更
                            </button>
                            <button
                              className="btn btn-sm btn-outline-secondary py-0"
                              disabled={busyId === item.id}
                              onClick={() => reopen(item)}
                            >
                              紐付けを外す
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="small">
                            {item.storeHint ? (
                              <>
                                フォルダの申告：<strong>{item.storeHint}</strong>
                              </>
                            ) : (
                              <span className="text-muted">フォルダから店名が読めていません</span>
                            )}
                          </div>
                          {item.reviewedAt && (
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {formatDate(item.reviewedAt)} に「店舗なし」で確定
                            </div>
                          )}
                          <div className="mt-1 d-flex flex-wrap gap-2">
                            <button
                              className="btn btn-sm btn-primary py-0"
                              disabled={busyId === item.id}
                              onClick={() => setPickingId(pickingId === item.id ? null : item.id)}
                            >
                              店舗を選ぶ
                            </button>
                            {item.reviewedAt ? (
                              <button
                                className="btn btn-sm btn-outline-secondary py-0"
                                disabled={busyId === item.id}
                                onClick={() => reopen(item)}
                              >
                                未判定に戻す
                              </button>
                            ) : (
                              <button
                                className="btn btn-sm btn-outline-secondary py-0"
                                disabled={busyId === item.id}
                                onClick={() => markNoStore(item)}
                              >
                                店舗なしで確定
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      {pickingId === item.id && (
                        <div className="mt-2">
                          <StorePicker
                            item={item}
                            onPick={(store) => assign(item, store)}
                            onCancel={() => setPickingId(null)}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="small text-muted">
          {total}件中 {total === 0 ? 0 : offset + 1}–{Math.min(offset + PAGE_SIZE, total)}
        </div>
        <div className="btn-group">
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={offset === 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            前へ
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            次へ
          </button>
        </div>
      </div>
    </Layout>
  );
}
